import { Box, Grid, Paper, Typography } from '@mui/material';
import { DebentureLiabilitySeries } from 'src/types/operations/debentures';

import {
  formatLiabilityDate,
  formatLiabilityDecimal,
  formatLiabilityQuantity,
  formatLiabilityText,
} from '../utils';

interface OperationDebenturesLiabilityQuotePanelProps {
  series: DebentureLiabilitySeries;
}

type PanelField = {
  label: string;
  value: string;
};

function PanelSection({
  title,
  fields,
}: {
  title: string;
  fields: PanelField[];
}) {
  return (
    <Grid item xs={12} md={4}>
      <Typography
        variant="caption"
        sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
        {title}
      </Typography>
      {fields.map(({ label, value }) => (
        <Box key={label} mt={1}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
            {value}
          </Typography>
        </Box>
      ))}
    </Grid>
  );
}

export default function OperationDebenturesLiabilityQuotePanel({
  series,
}: OperationDebenturesLiabilityQuotePanelProps) {
  const title = [series.classe, series.serie].filter(Boolean).join(' — ');

  return (
    <Paper square sx={{ p: 2, mb: 2, backgroundColor: '#f7f7f7' }}>
      <Typography
        variant="subtitle2"
        sx={(theme) => ({
          fontWeight: theme.typography.fontWeightBold,
          mb: 1,
          color: 'text.primary',
        })}>
        {title || '—'}
      </Typography>

      <Grid container spacing={2}>
        <PanelSection
          title="COTAS"
          fields={[
            {
              label: 'Quantidade de debêntures',
              value: formatLiabilityQuantity(series.quantidadeEmitida),
            },
          ]}
        />
        <PanelSection
          title="FLUXO E RENTABILIDADE"
          fields={[
            {
              label: 'Rendimentos distribuídos',
              value: formatLiabilityDecimal(series.rendimentos),
            },
            {
              label: 'Amortizações realizadas',
              value: formatLiabilityDecimal(series.amortizacoes),
            },
            {
              label: 'Rentabilidade',
              value: formatLiabilityText(series.taxaJuros),
            },
          ]}
        />
        <PanelSection
          title="OUTROS"
          fields={[
            { label: 'Código ISIN', value: formatLiabilityText(series.isin) },
            {
              label: 'Código da Negociação',
              value: formatLiabilityText(series.codigoAtivo),
            },
            {
              label: 'Data de Vencimento',
              value: formatLiabilityDate(series.dataVencimento),
            },
            {
              label: 'Tipo de Oferta',
              value: formatLiabilityText(series.tipoOferta),
            },
            {
              label: 'Situação da Debênture',
              value: formatLiabilityText(series.situacao),
            },
            {
              label: 'Periodicidade do Pagamento',
              value: formatLiabilityText(series.pagamentoPeriodicidade),
            },
          ]}
        />
      </Grid>
    </Paper>
  );
}
